import { Repeat } from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { DatePicker } from '@/components/ui/date-picker';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { templateLabel } from '@/features/campaigns/types';
import { formatScheduleTime, resolveTemplateId } from '../../payload';
import {
  IS_CONTINUOUS,
  RETRY_INTERVAL_MAX,
  RETRY_INTERVAL_MIN,
  templateFeatures,
  TTS_TEMPLATES,
  type VoiceCampaignFormValues,
} from '../../types';

interface Props {
  values: VoiceCampaignFormValues;
  setValues: (patch: Partial<VoiceCampaignFormValues>) => void;
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[140px_1fr] gap-3 border-t border-border py-2 text-sm first:border-t-0">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="text-foreground">{value || <span className="text-muted-foreground">—</span>}</dd>
    </div>
  );
}

export function StepReview({ values, setValues }: Props) {
  const finalTemplateId = resolveTemplateId(values);
  const features = templateFeatures(values.templateId);
  const sched = values.scheduleDate ? new Date(values.scheduleDate) : undefined;
  /** Retries are template-bound — TTS Simple and Continuous skip them. */
  const showRetry =
    !features.hasTtsFlow && values.templateId !== '7' && !IS_CONTINUOUS(values.templateId);

  const scheduleSummary = values.sendLater
    ? formatScheduleTime(values.scheduleDate)
    : 'Send immediately';

  const contactSummary = (() => {
    if (values.baseFileMode === 'contactList') {
      const count = values.contactList
        .split(/\r?\n/)
        .map((s) => s.trim())
        .filter(Boolean).length;
      return `Pasted ${count} number${count === 1 ? '' : 's'}`;
    }
    if (values.baseFileMode === 'newBaseFile') {
      return `Upload "${values.baseName || 'new file'}"`;
    }
    return values.oldBaseId
      ? `Existing list #${values.oldBaseId}`
      : 'Existing list (none picked)';
  })();

  const ttsSummary = TTS_TEMPLATES.has(values.templateId)
    ? `${values.ttsRows.length} row${values.ttsRows.length === 1 ? '' : 's'} (${values.ttsRows.filter((r) => r.ttsType === 'prompt').length} prompt, ${values.ttsRows.filter((r) => r.ttsType === 'variable').length} variable)`
    : '';

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Schedule</CardTitle>
          <CardDescription>
            Send immediately, or pick a future date and time.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between rounded-md border border-border p-3">
            <div>
              <div className="text-sm font-medium">Schedule for later</div>
              <div className="text-xs text-muted-foreground">
                When off, the campaign starts as soon as you submit.
              </div>
            </div>
            <Switch
              checked={values.sendLater}
              onCheckedChange={(v) => setValues({ sendLater: v })}
            />
          </div>

          {values.sendLater && (
            <div className="space-y-2">
              <Label>
                Send at
                <span className="ml-1 text-destructive">*</span>
              </Label>
              <DatePicker
                value={sched}
                onChange={(d) =>
                  setValues({ scheduleDate: d ? d.toISOString() : '' })
                }
                placeholder="Pick a date"
              />
            </div>
          )}
        </CardContent>
      </Card>

      {showRetry && (
        <Card>
          <CardHeader>
            <CardTitle>Retry</CardTitle>
            <CardDescription>
              Re-dial unanswered or failed numbers up to N times, spaced by the chosen
              interval.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="retries" className="inline-flex items-center gap-2">
                  <Repeat className="h-3.5 w-3.5 text-muted-foreground" />
                  Retries (range: 0-2)
                </Label>
                <Input
                  id="retries"
                  inputMode="numeric"
                  min={0}
                  max={2}
                  value={String(values.retries)}
                  onChange={(e) => {
                    const raw = Number(e.target.value.replace(/\D/g, '')) || 0;
                    setValues({ retries: Math.min(2, Math.max(0, raw)) });
                  }}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="retry-interval">Retry interval (range: 15-60 min)</Label>
                <Input
                  id="retry-interval"
                  type="number"
                  min={RETRY_INTERVAL_MIN}
                  max={RETRY_INTERVAL_MAX}
                  step={1}
                  placeholder={`From (${RETRY_INTERVAL_MIN}-${RETRY_INTERVAL_MAX})`}
                  value={String(values.retryInterval)}
                  onChange={(e) =>
                    setValues({
                      retryInterval: Number(e.target.value.replace(/\D/g, '')) || 0,
                    })
                  }
                  onBlur={(e) => {
                    let value = Number(e.target.value);
                    if (!value || value < RETRY_INTERVAL_MIN) value = RETRY_INTERVAL_MIN;
                    if (value > RETRY_INTERVAL_MAX) value = RETRY_INTERVAL_MAX;
                    setValues({ retryInterval: value });
                  }}
                />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Review</CardTitle>
          <CardDescription>
            Double-check the details before submitting. You can step back to fix anything.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <dl>
            <Row label="Name" value={values.campaignName} />
            <Row
              label="Type"
              value={`${templateLabel(Number(finalTemplateId))} (#${finalTemplateId})`}
            />
            <Row label="Schedule" value={scheduleSummary} />
            {showRetry && (
              <Row
                label="Retries"
                value={`${values.retries} attempt(s), ${values.retryInterval} min apart`}
              />
            )}
            <Row label="Contacts" value={contactSummary} />
            <Row
              label="Locations"
              value={
                values.locationIds.length > 0
                  ? `${values.locationIds.length} selected`
                  : 'All locations'
              }
            />
            <Row
              label="Dedicated CLIs"
              value={
                values.selectedCLI.length > 0
                  ? `${values.selectedCLI.length} selected`
                  : 'Shared pool'
              }
            />
            {ttsSummary && <Row label="TTS rows" value={ttsSummary} />}
            <Row label="Webhook" value={values.sendWebhook ? 'Enabled' : 'Disabled'} />
            <Row
              label="SMS"
              value={[
                values.sendSmsSuccess && 'on success',
                values.sendSmsFail && 'on failure',
                values.sendSmsDtmf && 'on DTMF',
                values.sendSmsPatchSuccess && 'on patch success',
                values.sendSmsPatchFail && 'on patch failure',
              ]
                .filter(Boolean)
                .join(', ') || 'None'}
            />
          </dl>
        </CardContent>
      </Card>
    </div>
  );
}
